/**
 * SFrame141 – Immediately After Death: Phase Overview
 * Figma Frame 41
 *
 * Shows the 7 IAD steps with progress, grouped by status.
 * Uses the PhaseOverview reusable component wired to real API data.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PhaseOverview, type PhaseStepState } from '../settlement';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { SETTLEMENT_PHASE_CONFIGS } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';


const iadConfig = SETTLEMENT_PHASE_CONFIGS['iad'];


export default function SFrame141() {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const estateId = paramEstateId || null;
  const { loadPhaseProgress, error } = useSettlementSession(estateId);


  // Derive all unique categories from the config so filters start fully active
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    iadConfig.steps.forEach(s => {
      if (s.category) cats.add(s.category);
    });
    return Array.from(cats);
  }, []);


  // All categories active by default; toggling deactivates/reactivates
  const [activeFilters, setActiveFilters] = useState<string[]>(allCategories);


  const [steps, setSteps] = useState<PhaseStepState[]>(
    iadConfig.steps.map((step) => ({
      step,
      status: 'not-started',
      subTaskProgress: {
        completed: 0,
        total: step.subTasks?.reduce((sum, st) => sum + (st.checklistItems?.length || 0), 0) || 0,
      },
    }))
  );


  // Load progress from API
  useEffect(() => {
    if (!estateId) return;


    const load = async () => {
      const progressData = await loadPhaseProgress('iad');


      setSteps(
        iadConfig.steps.map((step) => {
          const progress = progressData.find((p) => p.stepId === step.id);
          const totalItems = step.subTasks?.reduce(
            (sum, st) => sum + (st.checklistItems?.length || 0),
            0
          ) || 0;


          return {
            step,
            status: (progress?.status as PhaseStepState['status']) || 'not-started',
            subTaskProgress: {
              completed: progress?.completedChecklistItems?.length || 0,
              total: totalItems,
            },
          };
        })
      );
    };
    load();
  }, [estateId, loadPhaseProgress]);


  // Map step IDs to their SFrame routes
  const stepRoutes: Record<string, string> = {
    'iad-1': '/sframe-124',
    'iad-2': '/sframe-134',
    'iad-3': '/sframe-137',
    'iad-4': '/sframe-138',
    'iad-5': '/sframe-126',
    'iad-6': '/sframe-129',
  };


  const handleStepClick = (stepId: string) => {
    const route = stepRoutes[stepId];
    if (route) {
      navigate(estateId ? `${route}/${estateId}` : route);
    }
  };


  // Toggle a category filter on/off
  const handleCategoryToggle = (category: string) => {
    setActiveFilters((prev) =>
      prev.includes(category) ? prev.filter((f) => f !== category) : [...prev, category]
    );
  };


  return (
    <>
      {error && (
        <div className={`${SETTLEMENT_PLANNER_LAYOUT.container} mb-4`}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        </div>
      )}

      <PhaseOverview
        title="Immediately After Death"
        subtitle="Review and complete essential tasks in the days immediately following death."
        layoutVariant="planner"
        sectionOrder="next-first"
        progressCompletedItemLabel="steps"
        steps={steps}
        activeFilters={activeFilters}
        onStepClick={handleStepClick}
        onCategoryClick={(category: string) => handleCategoryToggle(category)}
      />
    </>
  );
}
